import React, { useState, useEffect } from "react";
import { Plus, Trash2, GripVertical, Flag, AlertTriangle, Eye, EyeOff } from "lucide-react";
import BannerIcon, { BANNER_ICON_OPTIONS } from "../../components/BannerIcon";
import LocationAutocomplete from "../../components/LocationAutocomplete";

const SCHEDULE_COLS = [
  { key: "time", label: "TIME" },
  { key: "scene_num", label: "SC#" },
  { key: "int_ext", label: "I/E" },
  { key: "day_night", label: "D/N" },
  { key: "set_name", label: "Set" },
  { key: "description", label: "Description" },
  { key: "cast_ids", label: "Cast IDs" },
  { key: "pages", label: "Pages" },
  { key: "location", label: "Location" },
];

export default function TodaysScheduleSection({ form, setForm, project, locations, inputCls, selectCls, Section, helpers }) {
  const { parseDurationMin, fmtMinutes, parsePagesEighths, fmtPages, unionThresholdMin, mealPenaltyMinutes, bannerOffsetMinutes } = helpers;

  const addScene = () => setForm(f => ({ ...f, scenes: [...f.scenes, { type: "scene", scene_num: "", int_ext: "INT", day_night: "DAY", set_name: "", description: "", cast_ids: "", pages: "", duration: "", time: "", location: "" }] }));
  const addBanner = () => setForm(f => ({ ...f, scenes: [...f.scenes, { type: "banner", banner_label: "Lunch", banner_time: "", banner_duration: "", banner_location: "", banner_icon: "utensils" }] }));
  const updScene = (i, k, v) => setForm(f => ({ ...f, scenes: f.scenes.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const delScene = (i) => setForm(f => ({ ...f, scenes: f.scenes.filter((_, idx) => idx !== i) }));
  const moveScene = (from, to) => setForm(f => {
    if (from === to || from < 0 || to < 0 || from >= f.scenes.length || to >= f.scenes.length) return f;
    const next = [...f.scenes];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return { ...f, scenes: next };
  });
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const onDragStart = (i) => (e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (i) => (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverIdx(i); };
  const onDrop = (i) => (e) => { e.preventDefault(); if (dragIdx !== null) moveScene(dragIdx, i); setDragIdx(null); setDragOverIdx(null); };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };
  const [, setTick] = useState(0);
  useEffect(() => {
    const anyRunning = (form.scenes || []).some(s => (s.type || "scene") === "scene" && s.timer_started_at);
    if (!anyRunning) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [form.scenes]);

  const limit = unionThresholdMin();
  const violations = form.scenes.map((s, i) => ({ s, i })).filter(x => (x.s.type || "scene") === "banner" && mealPenaltyMinutes(x.s) > 0);

  return (
    <Section title="Today's Schedule">
      <div className="pt-4 space-y-2">
        {violations.length > 0 && (
          <div data-testid="meal-penalty-global-warning" className="flex items-start gap-3 bg-red-500/10 border border-red-500/40 rounded-lg p-3 text-xs text-red-300">
            <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-red-400 uppercase tracking-widest text-[11px] mb-1">Meal Penalty Warning &middot; {fmtMinutes(limit)} limit</p>
              <p>{violations.map(v => `${(v.s.banner_label || "meal").toUpperCase()} (+${fmtMinutes(bannerOffsetMinutes(v.s))})`).join(", ")} — more than {fmtMinutes(limit)} after general call. This triggers meal-penalty pay. Move it earlier before publishing.</p>
            </div>
          </div>
        )}
        {form.scenes.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#28303F]">
                  {["", "TIME", "SC#", "I/E", "D/N", "Set", "Description", "Cast IDs", "Pages", "Location", ""].map((h, idx) => {
                    if (idx === 0 || idx === 10) return <th key={idx} className="text-left text-gray-500 pb-2 pr-2 font-medium">{h}</th>;
                    const col = SCHEDULE_COLS[idx - 1];
                    const hid = (form.hidden_schedule_columns || []).includes(col.key);
                    return (
                      <th key={idx} className={`text-left text-gray-500 pb-2 pr-2 font-medium ${hid ? "opacity-40" : ""}`}>
                        <span className="inline-flex items-center gap-1">
                          {h}
                          <button type="button"
                            onClick={() => setForm(f => {
                              const cur = f.hidden_schedule_columns || [];
                              return { ...f, hidden_schedule_columns: cur.includes(col.key) ? cur.filter(k => k !== col.key) : [...cur, col.key] };
                            })}
                            title={hid ? "Show column on call sheet" : "Hide column from call sheet"}
                            data-testid={`schedule-col-toggle-${col.key}`}
                            className={`transition-opacity ${hid ? "text-gray-500 hover:text-amber-400" : "opacity-40 hover:opacity-100 hover:text-amber-400"}`}>
                            {hid ? <EyeOff size={10} /> : <Eye size={10} />}
                          </button>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="space-y-1">
                {form.scenes.map((s, i) => {
                  const isBanner = (s.type || "scene") === "banner";
                  // Drag ONLY from the grip handle — otherwise clicks+text-select
                  // inside the row kick off drags and break editing/scrolling.
                  const rowDropProps = { onDragOver: onDragOver(i), onDrop: onDrop(i), onDragEnd };
                  const handleProps = { draggable: true, onDragStart: onDragStart(i) };
                  const rowCls = `border-b border-[#28303F]/50 ${dragOverIdx === i && dragIdx !== null && dragIdx !== i ? "bg-amber-500/10" : ""} ${dragIdx === i ? "opacity-50" : ""}`;
                  const hiddenCols = form.hidden_schedule_columns || [];
                  const colCls = (k) => hiddenCols.includes(k) ? "opacity-40 pointer-events-none" : "";
                  if (isBanner) {
                    const penalty = mealPenaltyMinutes(s);
                    const rowExtra = penalty > 0 ? "!bg-red-500/5" : "";
                    const curIcon = s.banner_icon || "flag";
                    return (
                      <tr key={i} data-testid="schedule-row-banner" {...rowDropProps} className={`${rowCls} ${rowExtra}`}>
                        <td {...handleProps} className="py-1 pr-2 w-6 text-center cursor-grab text-gray-500 hover:text-amber-400 select-none" title="Drag grip to reorder"><GripVertical size={14} className="inline pointer-events-none" /></td>
                        <td className={`py-1 pr-2 w-20 ${colCls("time")}`}>
                          <input value={s.banner_time || ""} onChange={e => updScene(i, "banner_time", e.target.value)}
                            placeholder="13:00" title="Clock time (optional)"
                            data-testid="schedule-banner-time-input" className={inputCls} />
                        </td>
                        <td colSpan={7} className="py-1 pr-2">
                          <div className={`flex flex-wrap items-center gap-2 rounded px-3 py-1.5 border ${penalty > 0 ? "bg-red-500/10 border-red-500/40" : "bg-amber-500/10 border-amber-500/30"}`}>
                            <div className={`shrink-0 flex items-center gap-1.5 bg-[#0D0F12] border rounded px-2 py-1 ${penalty > 0 ? "border-red-500/40" : "border-[#28303F]"}`}>
                              <BannerIcon slug={curIcon} size={14} color={penalty > 0 ? "#fca5a5" : "#fcd34d"} />
                              <select data-testid="schedule-banner-icon-select" value={curIcon}
                                onChange={e => updScene(i, "banner_icon", e.target.value)}
                                title="Banner icon (shown inline on the call sheet)"
                                className={`bg-transparent text-xs focus:outline-none ${penalty > 0 ? "text-red-300" : "text-amber-300"}`}>
                                {BANNER_ICON_OPTIONS.map(o => <option key={o.slug} value={o.slug} className="bg-[#0D0F12]">{o.label}</option>)}
                                <option value="" className="bg-[#0D0F12]">— none —</option>
                              </select>
                            </div>
                            <input value={s.banner_label || ""} onChange={e => updScene(i, "banner_label", e.target.value)}
                              placeholder="e.g. Lunch, Company Move" data-testid="schedule-banner-label-input"
                              className={`flex-1 min-w-[120px] bg-transparent text-xs font-bold focus:outline-none ${penalty > 0 ? "text-red-300 placeholder-red-500/40" : "text-amber-300 placeholder-amber-500/40"}`} />
                            {penalty > 0 && (
                              <span data-testid="meal-penalty-badge" title="More than 6h after general call — potential union meal penalty"
                                className="flex items-center gap-1 text-[10px] font-bold text-red-300 bg-red-500/20 border border-red-500/40 px-2 py-0.5 rounded-full whitespace-nowrap">
                                <AlertTriangle size={10}/> +{fmtMinutes(penalty)} OVER
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={`py-1 pr-2 w-24 ${colCls("location")}`}>
                          <LocationAutocomplete value={s.banner_location || ""} onChange={(v) => updScene(i, "banner_location", v)}
                            locations={locations} strict maxChars={10}
                            dataTestid="schedule-banner-location-input" placeholder="Pick…" className={inputCls} />
                        </td>
                        <td className="py-1"><button onClick={() => delScene(i)} className="text-gray-600 hover:text-red-400 p-1"><Trash2 size={13} /></button></td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={i} data-testid="schedule-row-scene" {...rowDropProps} className={rowCls}>
                      <td {...handleProps} className="py-1 pr-2 w-6 text-center cursor-grab text-gray-500 hover:text-amber-400 select-none" title="Drag grip to reorder"><GripVertical size={14} className="inline pointer-events-none" /></td>
                      <td className={`py-1 pr-2 w-20 ${colCls("time")}`}><input value={s.time || ""} data-testid="schedule-scene-time-input" onChange={e => updScene(i, "time", e.target.value)} className={inputCls} placeholder="07:00" /></td>
                      <td className={`py-1 pr-2 w-12 ${colCls("scene_num")}`}><input value={s.scene_num} onChange={e => updScene(i, "scene_num", e.target.value)} className={inputCls} placeholder="1" /></td>
                      <td className={`py-1 pr-2 w-16 ${colCls("int_ext")}`}><select value={s.int_ext} onChange={e => updScene(i, "int_ext", e.target.value)} className={selectCls}><option>INT</option><option>EXT</option><option>INT/EXT</option></select></td>
                      <td className={`py-1 pr-2 w-16 ${colCls("day_night")}`}><select value={s.day_night} onChange={e => updScene(i, "day_night", e.target.value)} className={selectCls}><option>DAY</option><option>NIGHT</option><option>DUSK</option><option>DAWN</option></select></td>
                      <td className={`py-1 pr-2 ${colCls("set_name")}`}><input value={s.set_name} onChange={e => updScene(i, "set_name", e.target.value)} className={inputCls} placeholder="Kitchen" /></td>
                      <td className={`py-1 pr-2 ${colCls("description")}`}>
                        <textarea
                          value={s.description}
                          onChange={e => updScene(i, "description", e.target.value)}
                          onInput={e => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
                          rows={1}
                          placeholder="Brief scene description"
                          data-testid="schedule-scene-description-input"
                          className={`${inputCls} resize-y min-h-[26px] leading-tight`}
                        />
                      </td>
                      <td className={`py-1 pr-2 w-20 ${colCls("cast_ids")}`}><input value={s.cast_ids} onChange={e => updScene(i, "cast_ids", e.target.value)} className={inputCls} placeholder="1,2,3" /></td>
                      <td className={`py-1 pr-2 w-16 ${colCls("pages")}`}><input value={s.pages} onChange={e => updScene(i, "pages", e.target.value)} className={inputCls} placeholder="2 3/8" /></td>
                      <td className={`py-1 pr-2 w-24 ${colCls("location")}`}>
                        <LocationAutocomplete value={s.location || ""} onChange={(v) => updScene(i, "location", v)}
                          locations={locations} strict maxChars={10}
                          dataTestid="schedule-scene-location-input" placeholder="Pick…" className={inputCls} />
                      </td>
                      <td className="py-1"><button onClick={() => delScene(i)} className="text-gray-600 hover:text-red-400 p-1"><Trash2 size={13} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
              {form.scenes.some(s => (s.type || "scene") === "scene") && (
                <tfoot>
                  <tr className="border-t-2 border-amber-500/30 bg-[#0D0F12]/60" data-testid="schedule-totals-row">
                    <td colSpan={8} className="py-2 pr-2 text-right text-xs text-gray-500 uppercase tracking-wider font-mono">Day Total</td>
                    <td className="py-2 pr-2 text-xs text-amber-400 font-bold" data-testid="schedule-total-pages">
                      {fmtPages(form.scenes.reduce((a, s) => (s.type || "scene") === "scene" ? a + parsePagesEighths(s.pages) : a, 0))} pgs
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
        <div className="flex items-center gap-4 mt-2">
          <button data-testid="builder-add-scene-btn" onClick={addScene}
            className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm transition-colors">
            <Plus size={15} /> Add Scene
          </button>
          <button data-testid="builder-add-banner-btn" onClick={addBanner}
            className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm transition-colors">
            <Flag size={13} /> Add Banner
          </button>
          {form.scenes.length > 1 && (
            <p className="text-xs text-gray-600 ml-auto">Drag <GripVertical size={11} className="inline text-amber-400/70" /> to reorder</p>
          )}
        </div>
      </div>
    </Section>
  );
}
